import Model from '@ember-data/model';
import attr from 'ember-data/attr';
import { isEmpty } from '@ember/utils';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import RouterService from '@ember/routing/router-service';
import AuthService from 'cio/services/auth';
import AccountService from 'cio/services/account';
import { sanitize } from 'cio/utils/sanitize';
import { config } from 'cio/helpers/preview-whitelist';
import urlBuilder from 'cio/utils/url-builder';
import { tracked } from '@glimmer/tracking';
import { IntegrationType } from 'cio/utils/integrations/static-integrations';

interface Settings {
  allowed_values: unknown;
  default: false | null; // false is not required, null means required
  description: string;
  key: string;
  label: string;
  placeholder: string;
  secret: boolean;
  type: string;
}

export default class IntegrationModel extends Model {
  @service auth!: AuthService;
  @service account!: AccountService;
  @service router!: RouterService;

  @attr('string') category!: string;
  @attr('string') name!: string;
  @attr('boolean') beta!: boolean;
  @attr('string') imageURL?: string;
  @attr('string') overview!: string;
  @attr('string') description!: string;
  @attr('string') detailsPageDescription!: string;
  @attr('string') detailsPageTitle!: string;
  @attr('string') subtitle?: string;
  @attr('array') useCases!: string[];
  @attr('string') ctaLabel!: string;
  @attr('string') ctaUrl!: string;
  @attr('string') type!: IntegrationType;
  @attr('string') homepage?: string;
  @attr('array') settings?: Settings[];

  forceOrder = 0;
  @tracked nativeDataOutEnabled = false;
  hasFetchedNativeDataOutEnabled = false;

  @alias('id') dynamicSegment!: string;

  @computed('router.currentRouteName')
  get public() {
    if (['integrations-public', 'integrations-public.type'].includes(this.router.currentRouteName)) {
      return true;
    }

    return false;
  }

  @computed('account')
  get path() {
    if (!this.account.current || isEmpty(Object.keys(this.account.current))) {
      return 'integrations-public.type';
    }

    if (this.type === 'NativeDataOut') {
      return 'integrations.data_out.type';
    }

    return 'integrations.type';
  }

  @computed('type')
  get ctaHref() {
    if (this.type !== 'Experiment') {
      return this.ctaUrl;
    }

    let typeformParams: Record<string, string> = {
      email_address: this.account.current?.email,
      account_id: this.account.current?.id,
      user_id: this.auth.user?.id ?? '',
      integration_type: this.name,
      utm_source: this.public ? 'Public' : 'In-App',
      utm_content: this.category,
      utm_term: this.name,
      utm_campaign: 'Existing',
    };

    return urlBuilder(this.ctaUrl, typeformParams);
  }

  @computed('imageURL', 'homepage')
  get imageSrc() {
    if (this.imageURL) {
      return this.imageURL;
    }

    if (this.homepage) {
      return `https://logo.clearbit.com/${this.homepage.replace(/https?:\/\/(www\.)?/, '').replace(/\/$/, '')}`;
    }

    return '';
  }

  @computed('useCases')
  get parsedUseCases() {
    return this.useCases.map((uc) => {
      let cleanUseCase = sanitize(uc, config);

      return cleanUseCase.replace(
        /(\s|^)(https?:\/\/[A-z0-9-._~:\/?#\[\]@!$&'\(\)*+,;=]+)(\s|$)/,
        '$1<a href="$2" target="_blank" rel="noopener noreferrer">$2</a>$3',
      );
    });
  }

  // TODO: will be removed once data squad creates single API to check all integrations
  async checkNativeDataOutStatus() {
    // api returns 200 if there is an integration and 404 if there isn't an integration
    // if there isn't an integration, then it is not enabled and nothing needs to be done
    try {
      if (this.hasFetchedNativeDataOutEnabled || this.type !== IntegrationType.nativeDataOut) {
        return;
      }
      this.hasFetchedNativeDataOutEnabled = true;
      await this.store.findRecord('integrations/data-out-type', this.id, {
        adapterOptions: { type: this.id },
      });
      this.nativeDataOutEnabled = true;
    } catch {}
  }

  // TODO: will be removed once data squad creates single API to check all integrations
  get enabled() {
    return this.nativeDataOutEnabled || false;
  }

  @computed('type')
  get dataFlow() {
    return this.type === 'NativeDataOut' ? 'out' : '';
  }

  @computed('type')
  get publicType() {
    if (
      [
        IntegrationType.experiment,
        IntegrationType.native,
        IntegrationType.nativeDataOut,
        IntegrationType.zapier,
        IntegrationType.partnerBuilt,
      ].includes(this.type)
    ) {
      return IntegrationType.existing;
    }
    return this.type;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    integration: IntegrationModel;
  }
}
